import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { useEffect, useRef, useCallback } from "react";

/**
 * Real-time collaboration for comments with granular actions:
 * addComment, updateComment, deleteComment, addReply, updateReply, resolveComment
 *
 * The hook does NOT create or reshape your payloads.
 * It only applies the exact change you already computed in your screen.
 */
export default function useCommentsCollaboration(projectId, onRemoteUpdate) {
  const ydocRef = useRef(null);
  const yCommentsRef = useRef(null);
  const providerRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;

    const ydoc = new Y.Doc();
    const provider = new WebsocketProvider(
      process.env.REACT_APP_SOCKET_URL || "ws://localhost:1234", // change to wss://your-domain/ws in prod
      projectId,
      ydoc
    );
    const yComments = ydoc.getArray("comments");

    const observer = () => {
      onRemoteUpdate?.(yComments.toJSON());
    };
    yComments.observe(observer);
    observer(); // initial hydration

    ydocRef.current = ydoc;
    yCommentsRef.current = yComments;
    providerRef.current = provider;

    return () => {
      yComments.unobserve(observer);
      provider.destroy();
      ydoc.destroy();
    };
  }, [projectId, onRemoteUpdate]);

  const withY = useCallback((fn) => {
    const yArr = yCommentsRef.current;
    const ydoc = ydocRef.current;
    if (!yArr || !ydoc) return;
    fn(yArr, ydoc);
  }, []);

  // Add top-level comment
  const addComment = useCallback(
    (comment) => {
      withY((yArr, ydoc) => {
        ydoc.transact(() => yArr.push([comment]));
      });
    },
    [withY]
  );

  // Update a top-level comment (by id)
  const updateComment = useCallback(
    (updatedComment) => {
      if (!updatedComment?.id) return;
      withY((yArr, ydoc) => {
        const list = yArr.toJSON();
        const index = list.findIndex((c) => c.id === updatedComment.id);
        ydoc.transact(() => {
          if (index === -1) {
            yArr.push([updatedComment]); // upsert behavior
          } else {
            yArr.delete(index, 1);
            yArr.insert(index, [updatedComment]);
          }
        });
      });
    },
    [withY]
  );

  // Delete a top-level comment (by id)
  const deleteComment = useCallback(
    (id) => {
      withY((yArr, ydoc) => {
        const list = yArr.toJSON();
        const index = list.findIndex((c) => c.id === id);
        if (index !== -1) {
          ydoc.transact(() => yArr.delete(index, 1));
        }
      });
    },
    [withY]
  );

  // Add reply to a parent comment
  const addReply = useCallback(
    (parentId, reply) => {
      withY((yArr, ydoc) => {
        const list = yArr.toJSON();
        const index = list.findIndex((c) => c.id === parentId);
        if (index === -1) return;

        const parent = list[index];
        const updatedParent = {
          ...parent,
          replies: [...(parent.replies || []), reply],
        };

        ydoc.transact(() => {
          yArr.delete(index, 1);
          yArr.insert(index, [updatedParent]);
        });
      });
    },
    [withY]
  );

  // Update a reply inside a parent (merge partial fields)
  const updateReply = useCallback(
    (parentId, replyId, partial) => {
      withY((yArr, ydoc) => {
        const list = yArr.toJSON();
        const pIndex = list.findIndex((c) => c.id === parentId);
        if (pIndex === -1) return;

        const parent = list[pIndex];
        const replies = parent.replies || [];
        const rIndex = replies.findIndex((r) => r.id === replyId);
        if (rIndex === -1) return;

        const updatedReplies = replies.map((r) =>
          r.id === replyId ? { ...r, ...partial } : r
        );

        const updatedParent = { ...parent, replies: updatedReplies };

        ydoc.transact(() => {
          yArr.delete(pIndex, 1);
          yArr.insert(pIndex, [updatedParent]);
        });
      });
    },
    [withY]
  );

  // Resolve a top-level comment (defaults to { isResolved: true }, but you can pass more)
  const resolveComment = useCallback(
    (id, fields = { isResolved: true }) => {
      withY((yArr, ydoc) => {
        const list = yArr.toJSON();
        const index = list.findIndex((c) => c.id === id);
        if (index === -1) return;

        const updated = { ...list[index], ...fields };

        ydoc.transact(() => {
          yArr.delete(index, 1);
          yArr.insert(index, [updated]);
        });
      });
    },
    [withY]
  );

  return {
    addComment,
    updateComment,
    deleteComment,
    addReply,
    updateReply,
    resolveComment,
  };
}
